import { CheckBox, CheckBoxOutlineBlank, VideoFile } from "@mui/icons-material"
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Modal, SkeletonLoading } from "@/core/components"
import { useInfiniteSelectList } from "@/core/hooks"
import { store } from "@core/store"
import { AdvertisingContent, ContentAsset } from "../../interfaces"
import { CONTENTS_LIMIT_DEFAULT, contentsApi } from "../../store"
import { CONTENT_MAX_FILES, contentFileKind } from "../../utils/validateContent"
import { formatBytes } from "../../utils/contentsFormatting"
import { useAssetLink } from "../../hooks/useAssetLink"

export interface IPickedAsset {
  id: number
  sizeBytes: number
  label: string
}

export interface IAssetPickerDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (picked: IPickedAsset[]) => void
  /** Слоты, занятые НОВЫМИ файлами черновика (комбинированный кап). */
  reservedSlots: number
  /** Уже добавленные в черновик ID: повторный выбор не занимает слот. */
  existingAssetIds?: readonly number[]
}

interface PickerThumbProps {
  contentId: number
  asset: ContentAsset
  onMissingPair: (contentId: number, assetId: number) => void
}

const PickerImageThumb = ({
  contentId,
  asset,
  onMissingPair,
}: PickerThumbProps) => {
  const { state, onMediaError, retry } = useAssetLink({
    contentId,
    assetId: asset.id,
  })
  const missing = state.status === "error" && state.reason !== "load_error"
  useEffect(() => {
    if (missing) onMissingPair(contentId, asset.id)
  }, [missing, onMissingPair, contentId, asset.id])
  if (state.status === "loading") {
    return (
      <SkeletonLoading isLoading>
        <Box width={48} height={48} />
      </SkeletonLoading>
    )
  }
  if (state.status === "error") {
    return state.reason === "load_error" ? (
      <Button
        size="small"
        onClick={retry}
        aria-label={`Повторить загрузку ${asset.filename}`}
      >
        Повторить
      </Button>
    ) : (
      <VideoFile color="disabled" />
    )
  }
  return (
    <Box
      component="img"
      src={state.url}
      alt={asset.filename}
      loading="lazy"
      onError={onMediaError}
      sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1 }}
    />
  )
}

const PickerThumb = (props: PickerThumbProps) =>
  props.asset.media_type === "video" ? (
    <VideoFile />
  ) : (
    <PickerImageThumb {...props} />
  )

export const AssetPickerDialog: FC<IAssetPickerDialogProps> = ({
  open,
  onClose,
  onSubmit,
  reservedSlots,
  existingAssetIds = [],
}) => {
  const [search, setSearch] = useState("")
  const [picked, setPicked] = useState<IPickedAsset[]>([])
  const [capBlocked, setCapBlocked] = useState(false)
  // Quarantine pairs for this picker session: storage 404 may leave metadata
  // intact. Do not retry the same bad source after a fresh listing or reopen.
  const [missingPairs, setMissingPairs] = useState(() => new Set<string>())
  const reloadedMissingCount = useRef(0)

  // useInfiniteSelectList сам держит первую страницу на монтировании и
  // дедуп/hasMore по getId. Хук не умеет skip — запрос уходит сразу на
  // маунте КОМПОНЕНТА, поэтому родитель обязан монтировать диалог
  // УСЛОВНО: {pickerOpen && <AssetPickerDialog ... />}.
  const list = useInfiniteSelectList<AdvertisingContent>({
    fetchPage: async ({ limit, offset }) => {
      const request = store.dispatch(
        contentsApi.endpoints.getContents.initiate(
          { queryParams: { limit, offset } },
          { forceRefetch: true },
        ),
      )
      try {
        const res = await request.unwrap()
        return { items: res.items, total: res.count }
      } finally {
        request.unsubscribe()
      }
    },
    getId: (contentItem) => contentItem.id,
    pageSize: CONTENTS_LIMIT_DEFAULT,
  })

  const { reload } = list
  const onMissingPair = useCallback(
    (contentId: number, assetId: number) => {
      if (!open) return
      const key = `${contentId}:${assetId}`
      setMissingPairs((previous) =>
        previous.has(key) ? previous : new Set([...previous, key]),
      )
    },
    [open],
  )

  useEffect(() => {
    if (!open || missingPairs.size <= reloadedMissingCount.current) return
    reloadedMissingCount.current = missingPairs.size
    store.dispatch(
      contentsApi.util.invalidateTags([{ type: "Contents", id: "LIST" }]),
    )
    // This list owns local items and has already unsubscribed from RTK Query.
    reload()
  }, [open, missingPairs, reload])

  useEffect(() => {
    if (open) {
      setSearch("")
      setPicked([])
      setCapBlocked(false)
    }
  }, [open])

  // Плоский список, дедуплицированный по asset.id; метка — первый материал
  // либо «из N материалов» (FR-20).
  const flatAssets = useMemo(() => {
    const byId = new Map<number, { asset: ContentAsset; sources: number[] }>()
    for (const contentItem of list.items) {
      for (const asset of contentItem.assets) {
        // A missing content/source does not make a shared asset globally bad.
        if (missingPairs.has(`${contentItem.id}:${asset.id}`)) continue
        const existing = byId.get(asset.id)
        if (existing) {
          existing.sources.push(contentItem.id)
        } else {
          byId.set(asset.id, { asset, sources: [contentItem.id] })
        }
      }
    }
    return Array.from(byId.values()).map(({ asset, sources }) => ({
      asset,
      contentId: sources[0],
      label:
        sources.length > 1
          ? `из ${sources.length} материалов`
          : `из материала #${sources[0]}`,
    }))
  }, [list.items, missingPairs])

  const available = useMemo(
    () => new Map(flatAssets.map((item) => [item.asset.id, item])),
    [flatAssets],
  )
  const validPicked = picked.flatMap(({ id }) => {
    const item = available.get(id)
    return item
      ? [{ id, sizeBytes: item.asset.size_bytes, label: item.label }]
      : []
  })
  useEffect(() => {
    setPicked((previous) =>
      previous.every(({ id }) => available.has(id))
        ? previous
        : previous.filter(({ id }) => available.has(id)),
    )
  }, [available])

  const visible = flatAssets.filter(({ asset }) =>
    asset.filename.toLowerCase().includes(search.trim().toLowerCase()),
  )

  const combinedCount = (ids: number[]) =>
    reservedSlots + new Set([...existingAssetIds, ...ids]).size
  const overCap =
    combinedCount(validPicked.map(({ id }) => id)) > CONTENT_MAX_FILES

  const toggle = (item: (typeof flatAssets)[number]) => {
    const isPicked = validPicked.some((p) => p.id === item.asset.id)
    if (
      !isPicked &&
      combinedCount([...validPicked.map(({ id }) => id), item.asset.id]) >
        CONTENT_MAX_FILES
    ) {
      setCapBlocked(true)
      return
    }
    setCapBlocked(false)
    setPicked((prev) =>
      isPicked
        ? prev.filter((p) => p.id !== item.asset.id)
        : [
            ...prev,
            {
              id: item.asset.id,
              sizeBytes: item.asset.size_bytes,
              label: item.label,
            },
          ],
    )
  }

  return (
    <Modal
      title="Существующие файлы"
      open={open}
      onClose={onClose}
      dialogProps={{ maxWidth: "sm" }}
      actions={[
        <Button key="cancel" onClick={onClose}>
          Отмена
        </Button>,
        <Button
          key="submit"
          variant="contained"
          disabled={validPicked.length === 0 || overCap}
          onClick={() => {
            if (validPicked.length === 0 || overCap) return
            onSubmit(validPicked)
            onClose()
          }}
        >
          Выбрать
        </Button>,
      ]}
    >
      <Stack spacing={1}>
        <TextField
          label="Поиск по имени файла"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {list.error != null && (
          <Typography color="error" variant="body2">
            Не удалось загрузить список
          </Typography>
        )}
        <List dense disablePadding>
          {visible.map(({ asset, contentId, label }) => {
            const isChecked = validPicked.some((p) => p.id === asset.id)
            const supported =
              contentFileKind(
                new File([], asset.filename, { type: asset.content_type }),
              ) !== null
            return (
              <ListItem key={asset.id} disableGutters>
                <Checkbox
                  icon={<CheckBoxOutlineBlank />}
                  checkedIcon={<CheckBox />}
                  checked={isChecked}
                  onChange={() => toggle({ asset, contentId, label })}
                  inputProps={{ "aria-label": asset.filename }}
                  disabled={!supported}
                />
                {open && (
                  <PickerThumb
                    contentId={contentId}
                    asset={asset}
                    onMissingPair={onMissingPair}
                  />
                )}
                <Box ml={1}>
                  <Typography variant="body2">{asset.filename}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(asset.size_bytes)} · <span>{label}</span>
                  </Typography>
                </Box>
              </ListItem>
            )
          })}
        </List>
        {list.hasMore && (
          <Button onClick={list.loadMore} disabled={list.isFetching}>
            Показать ещё
          </Button>
        )}
        {(capBlocked || overCap) && (
          <Typography color="error" variant="body2">
            Не более {CONTENT_MAX_FILES} файлов в материале (новые +
            существующие)
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          Выбрано: {validPicked.length} (новых файлов в черновике:{" "}
          {reservedSlots})
        </Typography>
      </Stack>
    </Modal>
  )
}
